import React from 'react';

import { useMediaQuery } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';

import AvatarCircle from '../AvatarCircle';
import { OButton, OTooltip } from '@src/shared';

import { useRoomContext } from '@src/contexts';
import { useRoomActionsStore } from '@src/stores';

import SocketService from '@src/services/SocketService';
import { getTruncatedName } from '@src/utils';

import { ECommonSocketKeys } from '@src/types';

import './notificationMessage.scss';

interface NotificationMessageProps {
  message: string;
  title?: string;
  peerId?: string;
  peerName?: string;
  titleColor?: 'blue' | 'white';
  icon?: React.ReactNode;
  notificationLayout?: 'message' | 'inline';
  requestId?: string;
  onClick: () => void;
  isPip?: boolean;
  isConnectionNotification?: boolean;
}

const NotificationMessage: React.FC<NotificationMessageProps> = ({
  message,
  peerName = '',
  peerId = '',
  title,
  icon,
  titleColor = 'white',
  notificationLayout = 'message',
  requestId = '',
  isPip = false,
  isConnectionNotification = false,
  onClick = () => {},
}) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const isInlineMessage = notificationLayout === 'inline';

  const { currentPeer } = useRoomContext();
  const {
    shareScreenSettings: { onClick: onShareScreenClick },
  } = useRoomActionsStore(
    useShallow((state) => ({
      shareScreenSettings: state.shareScreenSettings,
    }))
  );

  const handleResponse = (status: boolean) => {
    SocketService?.emit(ECommonSocketKeys.ADMIT_ACTION, {
      requestId,
      status: status ? 'APPROVED' : 'REJECTED',
    });

    if (status) {
      if (currentPeer?.peer_info.peer_screen) {
        onShareScreenClick(false);
      }
      SocketService?.emit(ECommonSocketKeys.PEER_ACTION, {
        action: 'stopScreenSharingAll',
        peer_id: currentPeer?.peer_info.peer_id,
        broadcast: true,
      });
    }
  };

  return (
    <div
      className={`notification-message ${isInlineMessage ? 'inline-notification' : ''} ${isPip ? 'is-pip' : ''} ${isPip && isConnectionNotification ? 'connection-notification' : ''} ${requestId ? 'is-request' : ''}`}
      onClick={requestId ? () => {} : onClick}
    >
      <div className="notification-message-user">
        <div className="notification-message-header">
          {icon && !isInlineMessage ? (
            <div className="notification-message-icon">{icon}</div>
          ) : (
            <AvatarCircle peerId={peerId} name={peerName} avatarImgSize={32} />
          )}
        </div>

        <div className={`notification-message-body ${icon && !isInlineMessage ? 'with-icon' : ''}`}>
          <OTooltip title={title || peerName} placement="top">
            <p className={`title ${titleColor ? titleColor : ''}`}>{title ? title : getTruncatedName(peerName)}</p>
          </OTooltip>
          <p className={`message-text ${isInlineMessage ? 'inline-message' : ''}  ${isMobile ? 'mobile' : ''}`}>
            {message}
          </p>
        </div>
      </div>

      {isInlineMessage && icon && <div className="inline-notification-icon">{icon}</div>}
      {requestId && (
        <div className="flex gap-2">
          <OButton text={'Allow'} size="small" onClick={() => handleResponse(true)} />
          <OButton text={'Reject'} variant="danger" size="small" onClick={() => handleResponse(false)} />
        </div>
      )}
    </div>
  );
};

export default NotificationMessage;
